import React, { Component } from 'react';
import axios from 'axios';
import TopBar from './components/TopBar';
import Footer from './components/Footer';
import DeleteAccountModal from './components/DeleteAccountModal';
import ProfileFormColumn from './profile/ProfileFormColumn';
import ProfileQrPanel from './profile/ProfileQrPanel';
import PublicUrlQrCode from './profile/PublicUrlQrCode';
import WritingSettingsTab from './profile/WritingSettingsTab';
import PublicAuthorFeedComponent from './PublicAuthorFeed';
import TabsMenu from '../components/TabsMenu';
import { withRouter } from './navigation/withNavigation';
import { getAuthHeaders, getEffectiveRole, getTokenRole, setRoleViewOverride, urlMap } from '../utils';
import { applyThemePref } from '../utils/theme';
import { applyFontPref } from '../utils/fontSize';
import { saveUserPrefs, loadUserPrefs } from '../utils/userPrefs';
import { tWeb } from '../i18n/sharedTranslations';

const AUTOSAVE_DEBOUNCE_MS = 600;  // existing debounce
const AUTOSAVE_DELAY_MS = 1000;    // NEW: intentional delay before sending PUT

const canImpersonateRole = (role) => role === 'publisher' || role === 'admin';
const normalizeEffectiveRoleOverride = (value) => (value === 'publisher' || value === 'user' ? value : null);
const resolveRoleView = (tokenRole, savedOverride) => {
  if (tokenRole === 'publisher') return savedOverride === 'user' ? 'user' : 'publisher';
  if (tokenRole === 'admin') return savedOverride === 'publisher' || savedOverride === 'user' ? savedOverride : 'admin';
  return tokenRole || 'user';
};
const roleViewToEffectiveRoleOverride = (tokenRole, roleView) => {
  if (tokenRole === 'publisher') return roleView === 'user' ? 'user' : 'publisher';
  if (tokenRole === 'admin') return roleView === 'publisher' || roleView === 'user' ? roleView : null;
  return null;
};

class Profile extends Component {
  state = {
    // server fields
    email: '',
    role: getTokenRole() || 'user',
    roleView: getEffectiveRole() || 'user',
    name: '',
    phone_number: '',
    bio: '',
    // preferences — seeded from localStorage so values are instant on mount
    ...(() => {
      const p = loadUserPrefs();
      return {
        theme: p.theme,
        fontSize: p.fontSize,
        language: p.language,
        emailMarketing: p.emailMarketing,
        pomodoroMinutes: p.pomodoroMinutes,
        feedPreviewUrl: p.feedPreviewUrl,
        effectiveRole: p.effectiveRole,
      };
    })(),
    // ui
    saveState: 'idle', // 'idle' | 'typing' | 'saving' | 'saved' | 'error'
    effectiveRoleSaveState: 'idle', // 'idle' | 'saving' | 'saved' | 'error'
    effectiveRoleSavePending: false,
    error: null,
    publicUrlInput: '',
    generatedPublicUrl: '',
    qrTransparentBackground: false,
    qrPreviewSize: 180,
    qrError: null,
    showDeleteModal: false,
    profileTab: 'profile', // 'profile' | 'writing' | 'branding' | 'feedPreview' | 'subscribers'
    subscribers: [],
    subscribersTotal: 0,
    subscribersLoading: false,
    iframeCopied: false,
    publisherRoleRequest: null,
    publisherRoleRequestLoading: false,
    publisherRoleRequestSubmitting: false,
    publisherRoleRequestError: null,
    publisherRoleRequestMessage: '',
  };

  debouncer = null;
  isMountedFlag = false;
  qrPreviewRef = React.createRef();
  qrExportPngRef = React.createRef();
  qrExportSvgRef = React.createRef();

  loadSubscribers = async () => {
    if (this.state.subscribersLoading) return;
    this.setState({ subscribersLoading: true });
    try {
      const res = await axios.get(urlMap.mySubscribers(), { headers: getAuthHeaders() });
      this.setState({ subscribers: res.data.subscribers || [], subscribersTotal: res.data.total || 0 });
    } catch {
      // non-fatal
    } finally {
      this.setState({ subscribersLoading: false });
    }
  };

  handleProfileTabChange = (tab) => {
    this.setState({ profileTab: tab });
    if (tab === 'subscribers' && this.state.subscribers.length === 0) {
      this.loadSubscribers();
    }
  };

  getPublicFeedOrigin = () => {
    if (typeof window === 'undefined') return 'https://app.storyclub.ai';
    const host = String(window.location.hostname || '').toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') {
      return window.location.origin;
    }
    return 'https://app.storyclub.ai';
  };

  getPublicFeedUrl = (rawValue) => {
    const normalizedInputUrl = this.normalizeInputUrl(rawValue);
    if (normalizedInputUrl) {
      try {
        const parsed = new URL(normalizedInputUrl);
        if (parsed.pathname.toLowerCase().endsWith('/feed')) {
          return parsed.toString();
        }
      } catch {}
    }

    const slug = this.extractPublicSlug(rawValue);
    if (!slug) return '';
    return `${this.getPublicFeedOrigin()}/${encodeURIComponent(slug)}/feed`;
  };

  async componentDidMount() {
    this.isMountedFlag = true;    
    
    try {
      const { data } = await axios.get(urlMap.profile, {
        headers: getAuthHeaders(),
      });

      if (!this.isMountedFlag) return;

      const prefs = data?.user_preferences ?? {};
      const tokenRole = data?.role ?? getTokenRole() ?? 'user';
      const serverEffectiveRole = normalizeEffectiveRoleOverride(prefs?.effectiveRole);
      const resolvedRoleView = resolveRoleView(tokenRole, serverEffectiveRole);
      this.setState(
        {
          email: data?.email ?? '',
          role: tokenRole,
          roleView: resolvedRoleView,
          effectiveRole: serverEffectiveRole,
          name: data?.name ?? '',
          phone_number: data?.phone_number ?? '',
          bio: typeof prefs.bio === 'string' ? prefs.bio : '',
          publicUrlInput: this.extractPublicSlug(data?.public_url ?? ''),
          theme: prefs.theme ?? 'system',
          fontSize: prefs.fontSize ?? 'medium',
          language: prefs.language ?? 'en',
          emailMarketing: !!prefs.emailMarketing,
          pomodoroMinutes: Number(prefs.pomodoroMinutes) > 0 ? Number(prefs.pomodoroMinutes) : 25,
          feedPreviewUrl: typeof prefs.feedPreviewUrl === 'string' ? prefs.feedPreviewUrl : '',
        },
        // Apply theme + font size after state reflects server prefs
        () => {
          const effectiveRoleOverride = roleViewToEffectiveRoleOverride(tokenRole, resolvedRoleView);
          saveUserPrefs({
            theme: this.state.theme,
            fontSize: this.state.fontSize,
            language: this.state.language,
            emailMarketing: this.state.emailMarketing,
            pomodoroMinutes: this.state.pomodoroMinutes,
            feedPreviewUrl: this.state.feedPreviewUrl,
            effectiveRole: effectiveRoleOverride,
          });
          setRoleViewOverride(effectiveRoleOverride);
          applyThemePref(this.state.theme);
          applyFontPref(this.state.fontSize);
        }
      );
      this.loadPublisherRoleRequest();
    } catch {
      // non-fatal: keep defaults
    }
  }

  loadPublisherRoleRequest = async () => {
    this.setState({ publisherRoleRequestLoading: true, publisherRoleRequestError: null });
    try {
      const res = await fetch(urlMap.myPublisherRoleRequest, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.error || `HTTP_${res.status}`);
      this.setState({ publisherRoleRequest: payload?.request ?? null, publisherRoleRequestLoading: false });
    } catch (err) {
      this.setState({ publisherRoleRequestError: err?.message || 'Could not load publisher request status.', publisherRoleRequestLoading: false });
    }
  };

  requestPublisherRole = async () => {
    this.setState({ publisherRoleRequestSubmitting: true, publisherRoleRequestError: null, publisherRoleRequestMessage: '' });
    try {
      const res = await fetch(urlMap.myPublisherRoleRequest, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 409) throw new Error(payload?.error || `HTTP_${res.status}`);

      if (res.status === 409) {
        this.setState({ publisherRoleRequestMessage: 'You already have publisher access or this account cannot request it.' });
      } else {
        this.setState({ publisherRoleRequest: payload?.request ?? null, publisherRoleRequestMessage: 'Request sent. Admin will review it shortly.' });
      }
      await this.loadPublisherRoleRequest();
    } catch (err) {
      this.setState({ publisherRoleRequestError: err?.message || 'Could not submit publisher role request.' });
    } finally {
      this.setState({ publisherRoleRequestSubmitting: false });
    }
  };

  formatPublisherRequestStatus = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (!normalized) return 'Unknown';
    return normalized
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  componentWillUnmount() {
    this.isMountedFlag = false;
    if (this.debouncer) clearTimeout(this.debouncer);
  }

  handleChange = (e) => {
    const { name, type } = e.target;
    const value = type === 'checkbox' ? e.target.checked : e.target.value;

    const extraState = name === 'publicUrlInput' ? { qrError: null } : {};

    this.setState(
      { [name]: value, saveState: 'typing', error: null, ...extraState },
      () => {
        // Live-preview theme / font size immediately on change
        if (name === 'theme') applyThemePref(this.state.theme);
        if (name === 'fontSize') applyFontPref(this.state.fontSize);
        this.queueAutosave();
      }
    );
  };

  queueAutosave = () => {
    if (this.debouncer) clearTimeout(this.debouncer);
    this.debouncer = setTimeout(this.autosave, AUTOSAVE_DEBOUNCE_MS);
  };

  // Small helper for delaying saves
  sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  autosave = async () => {    

    const { name, phone_number, bio, publicUrlInput, theme, fontSize, language, emailMarketing, pomodoroMinutes, feedPreviewUrl, role, roleView } = this.state;
    const effectiveRoleOverride = roleViewToEffectiveRoleOverride(role, roleView);
    const slug = String(publicUrlInput || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    const public_url = slug ? `https://storyclub.ai/w/${slug}` : null;
    this.setState((prev) => ({
      saveState: 'saving',
      error: null,
      effectiveRoleSaveState: prev.effectiveRoleSavePending ? 'saving' : prev.effectiveRoleSaveState,
    }));

    try {
      // wait a bit before hitting the server (coalesce rapid edits)
      await this.sleep(AUTOSAVE_DELAY_MS);

      await axios.put(
        urlMap.profile,
        {
          // full update endpoint: editable fields
          name,
          phone_number,
          public_url,
          user_preferences: {
            bio,
            theme,
            fontSize,
            language,
            emailMarketing,
            pomodoroMinutes,
            feedPreviewUrl,
            effectiveRole: effectiveRoleOverride,
          },
        },
        { headers: getAuthHeaders() }
      );

      // Persist all prefs to localStorage and align DOM
      saveUserPrefs({
        theme,
        fontSize,
        language,
        emailMarketing,
        pomodoroMinutes,
        feedPreviewUrl,
        effectiveRole: effectiveRoleOverride,
      });
      setRoleViewOverride(effectiveRoleOverride);
      applyThemePref(theme);

      if (!this.isMountedFlag) return;
      this.setState((prev) => ({
        saveState: 'saved',
        effectiveRoleSaveState: prev.effectiveRoleSavePending ? 'saved' : prev.effectiveRoleSaveState,
        effectiveRoleSavePending: false,
      }));
      setTimeout(() => {
        if (this.isMountedFlag && this.state.saveState === 'saved') {
          this.setState({ saveState: 'idle' });
        }
      }, 1500);
      setTimeout(() => {
        if (this.isMountedFlag && this.state.effectiveRoleSaveState === 'saved') {
          this.setState({ effectiveRoleSaveState: 'idle' });
        }
      }, 1800);
    } catch {
      if (!this.isMountedFlag) return;
      this.setState((prev) => ({
        saveState: 'error',
        error: 'Failed to save changes.',
        effectiveRoleSaveState: prev.effectiveRoleSavePending ? 'error' : prev.effectiveRoleSaveState,
        effectiveRoleSavePending: false,
      }));
    }
  };

  handleLogout = () => {
    try {
      localStorage.removeItem('token');
      setRoleViewOverride(null);
    } catch {}
    this.props.navigate('/');
  };

  handleRoleViewChange = (e) => {
    const { role } = this.state;
    if (!canImpersonateRole(role)) return;

    const requestedRoleView = String(e?.target?.value || '').toLowerCase();
    let nextRoleView = role;

    if (role === 'publisher') {
      nextRoleView = requestedRoleView === 'user' ? 'user' : 'publisher';
    } else if (role === 'admin') {
      nextRoleView = requestedRoleView === 'publisher' || requestedRoleView === 'user'
        ? requestedRoleView
        : 'admin';
    }

    this.setState(
      {
        roleView: nextRoleView,
        effectiveRole: roleViewToEffectiveRoleOverride(role, nextRoleView),
        saveState: 'typing',
        effectiveRoleSaveState: 'saving',
        effectiveRoleSavePending: true,
        error: null,
      },
      () => {
        setRoleViewOverride(roleViewToEffectiveRoleOverride(role, nextRoleView));
        this.queueAutosave();
      }
    );
  };

  handleFontSizeSelect = (fontSize) => {
    this.setState(
      { fontSize, saveState: 'typing', error: null },
      () => {
        applyFontPref(this.state.fontSize);
        this.queueAutosave();
      }
    );
  };

  handlePomodoroSelect = (pomodoroMinutes) => {
    this.setState(
      { pomodoroMinutes, saveState: 'typing', error: null },
      () => this.queueAutosave(),
    );
  };

  handleQrBackgroundModeChange = (transparent) => {
    this.setState({ qrTransparentBackground: Boolean(transparent), qrError: null });
  };

  handleQrPreviewSizeChange = (size) => {
    const parsed = Number(size);
    if (!Number.isFinite(parsed)) return;
    const qrPreviewSize = Math.max(72, Math.min(420, Math.round(parsed)));
    this.setState({ qrPreviewSize });
  };

  normalizeInputUrl = (rawValue) => {
    const raw = String(rawValue || '').trim();
    if (!raw) return null;

    const withScheme = /^https?:\/\//i.test(raw)
      ? raw
      : (/^(www\.|[a-z0-9.-]+\.[a-z]{2,})(?:[/:?#]|$)/i.test(raw) ? `https://${raw}` : null);

    if (!withScheme) return null;

    try {
      return new URL(withScheme).toString();
    } catch {
      return null;
    }
  };

  extractPublicSlug = (rawValue) => {
    const raw = String(rawValue || '').trim().toLowerCase();
    if (!raw) return '';

    const normalizedInputUrl = this.normalizeInputUrl(raw);
    if (normalizedInputUrl) {
      try {
        const parsed = new URL(normalizedInputUrl);
        const host = String(parsed.hostname || '').toLowerCase().replace(/^www\./, '');
        const segments = String(parsed.pathname || '')
          .split('/')
          .map((part) => part.trim().toLowerCase())
          .filter(Boolean);

        if (host.endsWith('storyclub.ai')) {
          if (segments[0] === 'w' && segments[1]) {
            return this.extractPublicSlug(segments[1]);
          }
          if (segments[0] === 'writers' && segments[1]) {
            return this.extractPublicSlug(segments[1]);
          }
          if (segments[1] === 'feed' && segments[0]) {
            return this.extractPublicSlug(segments[0]);
          }
          if (host !== 'storyclub.ai' && host !== 'app.storyclub.ai') {
            return this.extractPublicSlug(host.replace(/\.storyclub\.ai$/, ''));
          }
        }

        return '';
      } catch {
        return '';
      }
    }

    // Accept slug, legacy subdomain URLs, and path URLs, then normalize to a slug.
    let candidate = raw
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split(/[?#]/)[0];

    if (/^[a-z0-9-]+\.storyclub\.ai(?:\/.*)?$/.test(candidate)) {
      candidate = candidate.replace(/\.storyclub\.ai(?:\/.*)?$/, '');
    } else {
      const pathMatch = candidate.match(/(?:^|\.)storyclub\.ai\/(.+)$/);
      if (pathMatch?.[1]) {
        candidate = pathMatch[1];
      }
    }

    if (candidate.startsWith('writers/')) {
      candidate = candidate.slice('writers/'.length);
    }

    candidate = candidate.split('/')[0] || candidate;

    return candidate
      .replace(/[._\s/]+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '')
      .slice(0, 63);
  };

  normalizePublicUrl = (rawValue) => {
    const normalizedInputUrl = this.normalizeInputUrl(rawValue);
    if (normalizedInputUrl) return normalizedInputUrl;

    const slug = this.extractPublicSlug(rawValue);
    if (!slug) return null;
    return `https://storyclub.ai/writers/${slug}`;
  };

  generatePublicUrlQr = () => {
    const { publicUrlInput } = this.state;
    const slug = String(publicUrlInput || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '');

    if (!slug) {
      this.setState({
        generatedPublicUrl: '',
        qrError: 'Enter a slug (e.g. your-name).',
      });
      return;
    }

    const url = `https://storyclub.ai/w/${slug}`;
    this.setState({
      publicUrlInput: slug,
      generatedPublicUrl: url,
      qrError: null,
    });
  };

  downloadQrCode = () => {
    const canvas = this.qrExportPngRef.current?.querySelector('canvas')
      || this.qrPreviewRef.current?.querySelector('canvas');
    if (!canvas) {
      this.setState({ qrError: 'Generate a QR code before downloading.' });
      return;
    }

    const link = document.createElement('a');
    link.download = 'storyclub-public-url-qr-3000px.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  downloadQrCodeSvg = () => {
    const svgNode = this.qrExportSvgRef.current?.querySelector('svg');
    if (!svgNode) {
      this.setState({ qrError: 'Generate a QR code before downloading.' });
      return;
    }

    const serializer = new XMLSerializer();
    const svgText = serializer.serializeToString(svgNode);
    const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.download = 'storyclub-public-url-qr.svg';
    link.href = blobUrl;
    link.click();

    URL.revokeObjectURL(blobUrl);
  };

  getQrColors = () => {
    const { qrTransparentBackground } = this.state;
    const fgColor = '#FFFFFF';
    const bgColor = qrTransparentBackground ? 'transparent' : '#6D28D9';
    return { fgColor, bgColor };
  };

  getIframeCode = (feedUrl) => {
    if (!feedUrl) return '';
    return `<iframe src="${feedUrl}" width="100%" height="720" frameborder="0" scrolling="auto" style="border:none;border-radius:12px;overflow:hidden;"></iframe>`;
  };

  handleCopyIframe = (feedUrl) => {
    const code = this.getIframeCode(feedUrl);
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      this.setState({ iframeCopied: true });
      setTimeout(() => {
        if (this.isMountedFlag) this.setState({ iframeCopied: false });
      }, 2000);
    }).catch(() => {});
  };

  handleFeedPreviewUrlChange = (e) => {
    const { value } = e.target;
    this.setState(
      { feedPreviewUrl: value, saveState: 'typing', error: null },
      () => this.queueAutosave(),
    );
  };

  handleResetFeedPreviewUrl = () => {
    this.setState(
      { feedPreviewUrl: '', saveState: 'typing', error: null },
      () => this.queueAutosave(),
    );
  };

  openDeleteModal = () => this.setState({ showDeleteModal: true });
  closeDeleteModal = () => this.setState({ showDeleteModal: false });

  buildDummyPreviewSessions = () => {
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const dateAt = (daysAgo, hour, minute) => {
      const date = new Date();
      date.setHours(hour, minute, 0, 0);
      date.setDate(date.getDate() - daysAgo);
      return date.toISOString();
    };

    const additionalPreviewSessions = [
      { daysAgo: 8, hour: 9, minute: 15, difficulty: 5, issueId: 'TASK-22', title: 'The midpoint reversal needs stronger consequences', description: 'The protagonist should lose a meaningful advantage when the midpoint plan fails.', question: 'What consequence did you add to the midpoint reversal?', answer: 'I made the failed plan expose the protagonist’s secret and cost her the support of her closest ally.' },
      { daysAgo: 8, hour: 18, minute: 40, difficulty: 4, issueId: 'TASK-22', title: 'The midpoint reversal needs stronger consequences', description: 'The protagonist should lose a meaningful advantage when the midpoint plan fails.', question: 'How did the later session deepen that consequence?', answer: 'I added a public confrontation that prevents the protagonist from quietly repairing the damage.' },
      { daysAgo: 6, hour: 11, minute: 5, difficulty: 3, issueId: 'TASK-18', title: 'The antagonist motivation arrives too late', description: 'Earlier scenes need clues that make the antagonist’s choices feel inevitable.', question: 'Which early clue did you introduce?', answer: 'I planted a damaged photograph in chapter two and tied it to the antagonist’s final demand.' },
      { daysAgo: 5, hour: 8, minute: 20, difficulty: 2, issueId: 'TASK-11', title: 'Scene transitions interrupt the chapter rhythm', description: 'Transitions should preserve momentum while making time and location changes clear.', question: 'What transition did you simplify?', answer: 'I replaced a paragraph of travel summary with one sensory detail that establishes the new location.' },
      { daysAgo: 5, hour: 20, minute: 10, difficulty: 3, issueId: 'TASK-11', title: 'Scene transitions interrupt the chapter rhythm', description: 'Transitions should preserve momentum while making time and location changes clear.', question: 'What did you improve in the evening pass?', answer: 'I aligned the final image of one scene with the opening image of the next scene.' },
      { daysAgo: 3, hour: 13, minute: 35, difficulty: 1, issueId: 'TASK-07', title: 'Supporting character goal is unclear', description: 'The supporting character needs a visible objective that creates useful friction.', question: 'How did you clarify the supporting character’s goal?', answer: 'I gave him a concrete deadline and let him refuse the protagonist’s safest option.' },
      { daysAgo: 2, hour: 9, minute: 30, difficulty: 2, issueId: 'TASK-09', title: 'Dialogue sounds similar across two key characters', description: 'Each voice should carry distinct rhythm and vocabulary.', question: 'What dialogue pattern did you revise?', answer: 'I removed qualifiers from one voice and added deliberate pauses to the other.' },
      { daysAgo: 2, hour: 19, minute: 25, difficulty: 4, issueId: 'TASK-09', title: 'Dialogue sounds similar across two key characters', description: 'Each voice should carry distinct rhythm and vocabulary.', question: 'What remained difficult in the second dialogue pass?', answer: 'The emotional beats still sounded too polished, so I introduced interruptions and incomplete thoughts.' },
    ].map((entry, index) => {
      const sessionNumber = index + 3;
      const createdAt = dateAt(entry.daysAgo, entry.hour, entry.minute);
      return {
        id: `preview-session-${sessionNumber}`,
        started_at: dateAt(entry.daysAgo, entry.hour, Math.max(0, entry.minute - 25)),
        client_source: index % 2 === 0 ? 'web' : 'mobile',
        status: 'completed',
        planned_writing_minutes: 25,
        actual_writing_seconds: 1380 + (index * 47),
        reflections: [{
          id: `preview-reflection-${sessionNumber}`,
          writer_session_id: `preview-session-${sessionNumber}`,
          issue_id: entry.issueId,
          issue_title: entry.title,
          issue_description: entry.description,
          difficulty_score: entry.difficulty,
          created_at: createdAt,
          question_history: [{
            question_index: 0,
            question_label: 'Question 1',
            resolved_question: entry.question,
            answer_text: entry.answer,
            source: 'generated',
            created_at: createdAt,
            answered_at: createdAt,
          }],
        }],
      };
    });

    return [
      {
        id: 'preview-session-1',
        started_at: new Date(now - dayMs).toISOString(),
        client_source: 'mobile',
        status: 'completed',
        planned_writing_minutes: 25,
        actual_writing_seconds: 1520,
        reflections: [
          {
            id: 'preview-reflection-1',
            writer_session_id: 'preview-session-1',
            issue_id: 'TASK-14',
            issue_title: 'Chapter opening lacks tension and a clear promise to the reader',
            issue_description: 'Readers need a stronger hook and clearer conflict in the first page.',
            difficulty_score: 4,
            created_at: new Date(now - dayMs + 22 * 60 * 1000).toISOString(),
            question_history: [
              {
                question_index: 0,
                question_label: 'Question 1',
                resolved_question: 'What did you change to raise tension in the opening?',
                answer_text: 'I moved the key conflict to the first paragraph and cut setup that delayed the hook.',
                source: 'generated',
                created_at: new Date(now - dayMs + 10 * 60 * 1000).toISOString(),
                answered_at: new Date(now - dayMs + 18 * 60 * 1000).toISOString(),
              },
              {
                question_index: 1,
                question_label: 'Question 2',
                resolved_question: 'What is the next revision step for this task?',
                answer_text: 'I will rewrite the final sentence of the opening scene so it points directly to the next obstacle.',
                source: 'generated',
                created_at: new Date(now - dayMs + 19 * 60 * 1000).toISOString(),
                answered_at: new Date(now - dayMs + 22 * 60 * 1000).toISOString(),
              },
            ],
          },
        ],
      },
      {
        id: 'preview-session-2',
        started_at: new Date(now - 2 * dayMs).toISOString(),
        client_source: 'web',
        status: 'completed',
        planned_writing_minutes: 30,
        actual_writing_seconds: 1705,
        reflections: [
          {
            id: 'preview-reflection-2',
            writer_session_id: 'preview-session-2',
            issue_id: 'TASK-09',
            issue_title: 'Dialogue sounds similar across two key characters',
            issue_description: 'Each voice should carry distinct rhythm and vocabulary.',
            difficulty_score: 2,
            created_at: new Date(now - 2 * dayMs + 20 * 60 * 1000).toISOString(),
            question_history: [
              {
                question_index: 0,
                question_label: 'Question 1',
                resolved_question: 'How did you differentiate the two voices in this session?',
                answer_text: 'I shortened one character\'s lines and gave the other more metaphor-heavy language.',
                source: 'generated',
                created_at: new Date(now - 2 * dayMs + 12 * 60 * 1000).toISOString(),
                answered_at: new Date(now - 2 * dayMs + 20 * 60 * 1000).toISOString(),
              },
            ],
          },
        ],
      },
      ...additionalPreviewSessions,
    ].sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
  };

  render() {
    const {
      email,
      role,
      roleView,
      effectiveRoleSaveState,
      name,
      phone_number,
      bio,
      theme,
      fontSize, language,
      emailMarketing,
      pomodoroMinutes,
      error,
      publicUrlInput,
      generatedPublicUrl,
      qrTransparentBackground,
      qrPreviewSize,
      qrError,
      profileTab,
      feedPreviewUrl,
      subscribers,
      subscribersTotal,
      subscribersLoading,
      iframeCopied,
      publisherRoleRequest,
      publisherRoleRequestLoading,
      publisherRoleRequestSubmitting,
      publisherRoleRequestError,
      publisherRoleRequestMessage,
    } = this.state;

    const publisherRequestStatus = String(publisherRoleRequest?.status || '').toLowerCase();
    const publicFeedUrl = this.getPublicFeedUrl(publicUrlInput);
    const effectiveFeedPreviewUrl = feedPreviewUrl || publicFeedUrl;
    const iframeCode = this.getIframeCode(effectiveFeedPreviewUrl);
    const { fgColor, bgColor } = this.getQrColors();
    const previewAuthorName = String(name || '').trim() || 'Author Name';
    const dummySessions = this.buildDummyPreviewSessions();
    const dummyProgress = dummySessions
      .flatMap((s) => (s.reflections || []).map((r) => ({
        id: r.id,
        writer_session_id: r.writer_session_id,
        difficulty_score: r.difficulty_score ?? null,
        created_at: r.created_at,
      })))
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const previewTotalWritingSeconds = dummySessions.reduce(
      (total, s) => total + (Number(s.actual_writing_seconds) || 0), 0,
    );
    const previewReflectionAnswerSeconds = dummySessions.reduce((sessionTotal, session) => {
      return sessionTotal + (session.reflections || []).reduce((reflTotal, reflection) => {
        const history = Array.isArray(reflection?.question_history) ? reflection.question_history : [];
        const answeredRows = history.filter((q) => q?.answered_at);
        if (answeredRows.length === 0) return reflTotal;
        const allCreatedMs = history.map((q) => new Date(q?.created_at || 0).getTime()).filter((t) => t > 0);
        const allAnsweredMs = answeredRows.map((q) => new Date(q?.answered_at || 0).getTime()).filter((t) => t > 0);
        if (allCreatedMs.length === 0 || allAnsweredMs.length === 0) return reflTotal;
        const diffMs = Math.max(...allAnsweredMs) - Math.min(...allCreatedMs);
        return diffMs > 0 ? reflTotal + Math.round(diffMs / 1000) : reflTotal;
      }, 0);
    }, 0);
    const previewData = {
      author: { name: previewAuthorName, slug: '', bio: '' },
      sessions: dummySessions,
      progress: dummyProgress,
      currentProject: { title: 'Sample project', projectProgressPercent: 64 },
      pagination: { page: 1, pageSize: 25, totalItems: dummySessions.length, totalPages: 1 },
      totalWritingSeconds: previewTotalWritingSeconds,
      reflectionAnswerSeconds: previewReflectionAnswerSeconds,
    };
    const t = (key, vars) => tWeb(language || 'en', key, vars);

    return (
      <div className="profile-page">
        <TopBar
          pageTitle="Profile"
          backTo="/home"
          backLabel="Back to work"
        />
        
        <main className="profile-main">
          <TabsMenu
            className="pf-page-tabs"
            ariaLabel="Profile sections"
            activeKey={profileTab}
            onChange={(key) => {
              if (key === 'feedPreview' || key === 'subscribers') this.handleProfileTabChange(key);
              else this.setState({ profileTab: key });
            }}
            tabs={[
              { key: 'profile', label: t('profile.tab.profile') },
              { key: 'writing', label: t('profile.tab.writing') },
              { key: 'branding', label: t('profile.tab.branding') },
              { key: 'feedPreview', label: t('profile.tab.feedPreview') },
              {
                key: 'subscribers',
                label: t('profile.tab.subscribers'),
                badge: subscribersTotal > 0 ? subscribersTotal : null,
                badgeClassName: 'pf-feed-section-tab__count',
              },
            ]}
          />

          {profileTab === 'writing' ? (
            <div className="profile-card">
              <WritingSettingsTab
                fontSize={fontSize}
                pomodoroMinutes={pomodoroMinutes}
                onFontSizeSelect={this.handleFontSizeSelect}
                onPomodoroSelect={this.handlePomodoroSelect}
              />
            </div>
          ) : profileTab === 'branding' ? (
            <div className="profile-card">
              <div className="pf-grid">
                <section className="pf-grid__col pf-grid__col--profile">
                  <div className="pf-row pf-row--compact pf-row--public-url">
                    <label htmlFor="public-url" className="pf-label">{t('profile.publicUrl')}</label>
                    <div className="pf-public-url-inline">
                      <div className="pf-public-url-field">
                        <span className="pf-public-url-prefix">storyclub.ai/w/</span>
                        <input
                          id="public-url"
                          name="publicUrlInput"
                          type="text"
                          className="pf-input pf-input--slug"
                          placeholder="your-slug"
                          value={publicUrlInput}
                          onChange={this.handleChange}
                        />
                      </div>
                      <button
                        type="button"
                        className="pf-btn dark pf-btn--qr-compact"
                        onClick={this.generatePublicUrlQr}
                      >
                        {t('profile.generateQr')}
                      </button>
                    </div>
                  </div>
                  {qrError && <div className="pf-error">{qrError}</div>}

                  <PublicUrlQrCode
                    generatedPublicUrl={generatedPublicUrl}
                    qrTransparentBackground={qrTransparentBackground}
                    qrPreviewSize={qrPreviewSize}
                    fgColor={fgColor}
                    bgColor={bgColor}
                    qrPreviewRef={this.qrPreviewRef}
                    qrExportPngRef={this.qrExportPngRef}
                    qrExportSvgRef={this.qrExportSvgRef}
                    onDownloadPng={this.downloadQrCode}
                    onDownloadSvg={this.downloadQrCodeSvg}
                    onQrBackgroundModeChange={this.handleQrBackgroundModeChange}
                    onQrPreviewSizeChange={this.handleQrPreviewSizeChange}
                  />
                </section>

                <ProfileQrPanel
                  qrTransparentBackground={qrTransparentBackground}
                  fgColor={fgColor}
                  bgColor={bgColor}
                />
              </div>
            </div>
          ) : profileTab === 'feedPreview' ? (
            <div className="profile-card">
              <div className="pf-feed-embed pf-feed-embed--full">
                <div className="pf-row pf-row--compact pf-row--public-url">
                  <label htmlFor="feed-preview-url" className="pf-label">{t('profile.liveUrl')}</label>
                  <div className="pf-public-url-inline">
                    <input
                      id="feed-preview-url"
                      type="text"
                      className="pf-input"
                      placeholder="https://app.storyclub.ai/w/your-slug/feed"
                      value={effectiveFeedPreviewUrl}
                      onChange={this.handleFeedPreviewUrlChange}
                    />
                    {!!feedPreviewUrl && (
                      <button
                        type="button"
                        className="pf-btn dark pf-btn--qr-compact"
                        onClick={this.handleResetFeedPreviewUrl}
                      >
                        {t('profile.resetToMyUrl')}
                      </button>
                    )}
                  </div>
                </div>

                {effectiveFeedPreviewUrl ? (
                  <>
                    <a href={effectiveFeedPreviewUrl} target="_blank" rel="noreferrer" className="pf-public-url__link">
                      {effectiveFeedPreviewUrl}
                    </a>
                    <div className="pf-embed-block">
                      <div className="pf-embed-block__header">
                        <span className="pf-embed-block__label">{t('profile.embedOnWebsite')}</span>
                        <button
                          type="button"
                          className={`pf-embed-block__copy${iframeCopied ? ' is-copied' : ''}`}
                          onClick={() => this.handleCopyIframe(effectiveFeedPreviewUrl)}
                        >
                          {iframeCopied ? t('profile.copied') : t('profile.copyCode')}
                        </button>
                      </div>
                      <pre className="pf-embed-block__code">{iframeCode}</pre>
                    </div>
                    <iframe
                      key={effectiveFeedPreviewUrl}
                      src={effectiveFeedPreviewUrl}
                      className="pf-feed-embed__frame"
                      title="Live public feed"
                    />
                  </>
                ) : (
                  <>
                    <p className="pf-public-url__hint">{t('profile.feedPreviewHint')}</p>
                    <div className="pf-feed-preview-shell" aria-label="Public author feed dummy preview">
                      <PublicAuthorFeedComponent previewData={previewData} />
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : profileTab === 'subscribers' ? (
            <div className="profile-card">
              <div className="pf-feed-embed pf-feed-embed--full">
                <div className="pf-subscribers">
                  {subscribersLoading ? (
                    <p className="pf-subscribers__empty">{t('profile.loadingSubscribers')}</p>
                  ) : subscribers.length === 0 ? (
                    <p className="pf-subscribers__empty">{t('profile.noSubscribers')}</p>
                  ) : (
                    <table className="pf-subscribers__table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Status</th>
                          <th>Source</th>
                          <th>Subscribed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscribers.map((s) => (
                          <tr key={s.id} className={s.status === 'unsubscribed' ? 'pf-subscribers__row--unsub' : ''}>
                            <td>{s.email}</td>
                            <td>{s.status}</td>
                            <td>{s.source || '—'}</td>
                            <td>{new Date(s.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          ) : (
          <form className="profile-card" onSubmit={(e) => e.preventDefault()}>
            <div className="pf-grid pf-grid--single">
              <ProfileFormColumn
                email={email}
                role={role}
                name={name}
                bio={bio}
                phone_number={phone_number}
                theme={theme}
                language={language}
                emailMarketing={emailMarketing}
                error={error}
                t={t}
                onFieldChange={this.handleChange}
              />
              {role !== 'publisher' && role !== 'admin' && (
                <section className="pf-grid__col pf-grid__col--profile" style={{ marginTop: 12 }}>
                  <div className="pf-row pf-row--compact pf-role-request">
                    <label className="pf-label">Publisher Role</label>
                    <p className="pf-role-request__hint">
                      Request publisher access to create and manage story scaffolds.
                    </p>
                    <button
                      type="button"
                      className="pf-btn dark pf-btn--no-wrap"
                      onClick={this.requestPublisherRole}
                      disabled={publisherRoleRequestSubmitting || publisherRoleRequestLoading || publisherRoleRequest?.status === 'pending'}
                    >
                      {publisherRoleRequestSubmitting
                        ? 'Sending...'
                        : publisherRoleRequest?.status === 'pending'
                          ? 'Request pending'
                          : 'Request publisher role'}
                    </button>
                    {publisherRoleRequest && (
                      <div className={`pf-status-chip pf-status-chip--${publisherRequestStatus || 'pending'}`}>
                        <span className="pf-status-chip__label">Request status</span>
                        <span className="pf-status-chip__value">{this.formatPublisherRequestStatus(publisherRequestStatus)}</span>
                      </div>
                    )}
                    {publisherRoleRequestMessage && (
                      <div className="pf-save saved">{publisherRoleRequestMessage}</div>
                    )}
                    {publisherRoleRequestError && (
                      <div className="pf-save error">{publisherRoleRequestError}</div>
                    )}
                  </div>
                </section>
              )}
            </div>
          </form>
          )}

          <div className="pf-bottom-actions">
            <button type="button" className="pf-btn dark" onClick={this.handleLogout}>
              {t('profile.logout')}
            </button>
          </div>

          <div className="pf-danger-zone">
            <h2 className="pf-danger-zone-title">{t('profile.dangerZone')}</h2>
            <p className="pf-danger-zone-desc">
              {t('profile.dangerText')}
            </p>
            <button
              type="button"
              className="pf-btn pf-btn--destructive"
              onClick={this.openDeleteModal}
            >
              {t('profile.deleteAccount')}
            </button>
          </div>

        </main>

        <DeleteAccountModal
          isOpen={this.state.showDeleteModal}
          onClose={this.closeDeleteModal}
        />

        <Footer />
      </div>
    );
  }
}

export default withRouter(Profile);
